import { api } from './api-client';
import type { Repository } from './repository';

export class CloudRepository implements Repository {
  // Foyer
  async getFoyer(): Promise<unknown | null> {
    return api.get('/foyer');
  }

  async saveFoyer(foyer: unknown): Promise<void> {
    if ((foyer as { createdBy?: unknown }).createdBy === undefined) {
      throw new Error('saveFoyer: createdBy is undefined — use createFoyer() factory');
    }
    await api.put('/foyer', foyer);
  }

  // Lines
  async getLines(_foyerId: string): Promise<unknown[]> {
    const data = await api.get<{ lines: unknown[] }>('/budget/data');
    return data.lines;
  }

  async saveLine(line: unknown): Promise<void> {
    const l = line as { id: string };
    await api.put(`/budget/lines/${l.id}`, line);
  }

  async deleteLine(id: string): Promise<void> {
    await api.delete(`/budget/lines/${id}`);
  }

  async saveLines(lines: unknown[]): Promise<void> {
    if (lines.length === 0) return;
    await api.put('/budget/lines', lines);
  }

  // Domains
  async getDomains(_foyerId: string): Promise<unknown[]> {
    const data = await api.get<{ domains: unknown[] }>('/budget/data');
    return data.domains;
  }

  async saveDomain(domain: unknown): Promise<void> {
    const d = domain as { id: string };
    await api.put(`/budget/domains/${d.id}`, domain);
  }

  async deleteDomain(id: string): Promise<void> {
    await api.delete(`/budget/domains/${id}`);
  }

  // Categories
  async getCategories(_foyerId: string): Promise<unknown[]> {
    const data = await api.get<{ categories: unknown[] }>('/budget/data');
    return data.categories;
  }

  async saveCategory(category: unknown): Promise<void> {
    const cat = category as { id: string };
    await api.put(`/budget/categories/${cat.id}`, category);
  }

  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/budget/categories/${id}`);
  }

  // Blocks
  async getBlocks(_foyerId: string): Promise<unknown[]> {
    const data = await api.get<{ blocks: unknown[] }>('/budget/data');
    return data.blocks;
  }

  async saveBlock(block: unknown): Promise<void> {
    const b = block as { id: string };
    await api.put(`/budget/blocks/${b.id}`, block);
  }

  // Snapshots
  async getBudgetSnapshots(_foyerId: string): Promise<unknown[]> {
    return api.get<unknown[]>('/budget/snapshots');
  }

  async createBudgetSnapshot(snapshot: unknown): Promise<void> {
    await api.post('/budget/snapshot', snapshot);
  }

  // Scenarios
  async getScenarios(_foyerId: string): Promise<unknown[]> {
    return api.get<unknown[]>('/scenarios');
  }

  async saveScenario(scenario: unknown): Promise<void> {
    if ((scenario as { createdBy?: unknown }).createdBy === undefined) {
      throw new Error('saveScenario: createdBy is undefined — use createScenario() factory');
    }
    const s = scenario as { id: string };
    await api.put(`/scenarios/${s.id}`, scenario);
  }

  async deleteScenario(id: string): Promise<void> {
    await api.delete(`/scenarios/${id}`);
  }

  // Objectives
  async getObjectives(_foyerId: string): Promise<unknown[]> {
    return api.get<unknown[]>('/objectifs');
  }

  async saveObjective(obj: unknown): Promise<void> {
    if ((obj as { createdBy?: unknown }).createdBy === undefined) {
      throw new Error('saveObjective: createdBy is undefined — use createObjective() factory');
    }
    const o = obj as { id: string };
    await api.put(`/objectifs/${o.id}`, obj);
  }

  async deleteObjective(id: string): Promise<void> {
    await api.delete(`/objectifs/${id}`);
  }

  // Patrimoine Lines
  async getPatrimoineLines(_foyerId: string): Promise<unknown[]> {
    const data = await api.get<{ lines: unknown[] }>('/patrimoine/data');
    return data.lines;
  }

  async savePatrimoineLine(line: unknown): Promise<void> {
    const l = line as { id: string };
    await api.put(`/patrimoine/lines/${l.id}`, line);
  }

  async deletePatrimoineLine(id: string): Promise<void> {
    await api.delete(`/patrimoine/lines/${id}`);
  }

  // Patrimoine Categories
  async getPatrimoineCategories(_foyerId: string): Promise<unknown[]> {
    const data = await api.get<{ categories: unknown[] }>('/patrimoine/data');
    return data.categories;
  }

  async savePatrimoineCategory(cat: unknown): Promise<void> {
    const c = cat as { id: string };
    await api.put(`/patrimoine/categories/${c.id}`, cat);
  }

  async deletePatrimoineCategory(id: string): Promise<void> {
    await api.delete(`/patrimoine/categories/${id}`);
  }

  // Patrimoine Snapshots
  async getPatrimoineSnapshots(_foyerId: string): Promise<unknown[]> {
    return api.get<unknown[]>('/patrimoine/snapshots');
  }

  async createPatrimoineSnapshot(snapshot: unknown): Promise<void> {
    await api.post('/patrimoine/snapshot', snapshot);
  }

  async deletePatrimoineSnapshot(id: string): Promise<void> {
    await api.delete(`/patrimoine/snapshots/${id}`);
  }

  // KPI Thresholds
  async getKpiThresholds(_foyerId: string): Promise<unknown | null> {
    return api.get('/settings/thresholds');
  }

  async saveKpiThresholds(thresholds: unknown): Promise<void> {
    await api.put('/settings/thresholds', thresholds);
  }

  // Locks
  async acquireLock(entity: 'budget' | 'patrimoine', _userId: string): Promise<{ acquired: boolean; lockedBy?: string }> {
    const res = await api.post<{ acquired: boolean; lock?: { lockedBy: string; since: string } }>('/locks/acquire', { entity });
    return { acquired: res.acquired, lockedBy: res.lock?.lockedBy };
  }

  async releaseLock(entity: 'budget' | 'patrimoine'): Promise<void> {
    await api.post('/locks/release', { entity });
  }

  async heartbeat(entity: 'budget' | 'patrimoine'): Promise<void> {
    await api.post('/locks/heartbeat', { entity });
  }

  async getLockStatus(entity: 'budget' | 'patrimoine'): Promise<{ locked: boolean; lockedBy?: string }> {
    const res = await api.get<{ locked: boolean; lock?: { userId: string; userName: string; acquiredAt: string; lastHeartbeat: string } }>(`/locks/status?entity=${entity}`);
    return { locked: res.locked, lockedBy: res.lock?.userName };
  }

  // Export/Import
  async exportAll(): Promise<unknown> {
    return api.get('/export');
  }

  async importAll(data: unknown): Promise<void> {
    await api.post('/import', data);
  }

  async deleteAll(): Promise<void> {
    await api.delete('/foyer');
  }
}
